import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase, {
  isSandboxModeActive,
} from '../utils/nodeAxiosFirebase';
import moment from 'moment';
import { globalThrottleMap } from '../hooks/refreshThrottleMap';
import { invalidateStructureQueries } from '../config/queryClient';
import { toast } from 'react-toastify';

// Resolve a block from the structure indexes returned by the API back to its
// Redux array position.
const findBlockSlot = (elementData, tabIndex, blockIndex) => {
  if (!Array.isArray(elementData)) return null;
  const tabPos = elementData.findIndex((tab) => tab?.index === tabIndex);
  const tab = elementData[tabPos >= 0 ? tabPos : tabIndex];
  if (!tab?.blocks) return null;
  const blockPos = tab.blocks.findIndex(
    (block) => block?.structureBlockIndex === blockIndex
  );
  const resolvedPos = blockPos >= 0 ? blockPos : blockIndex;
  if (!tab.blocks[resolvedPos]) return null;
  return { tab, blockPos: resolvedPos };
};

// Structure refreshes can land out of order (slow network, retryUntilChanged):
// a reply must never overwrite blocks or totals already written by a request
// started after it, e.g. the refresh that brought a freshly added item.
let blocksRefreshSeq = 0;
const appliedBlocksRefresh = new Map();

const updateEditedCell = (cell, key, value, lang) => {
  cell.value = value;
  cell.transformedValue = value;

  const findSelection = (selectedValue) => cell.selections?.find((option) => {
    const optionValue = option?.value ?? option?.id;
    return optionValue != null && selectedValue != null && String(optionValue) === String(selectedValue);
  });
  const getSelectionLabel = (selection, selectedValue) =>
    selection?.[`label_${lang}`] ?? selection?.label ?? selection?.name ?? selectedValue;

  if (['selection', 'dropdown', 'select', 'status', 'slider'].includes(cell.typeValue)) {
    const selection = findSelection(value);

    // Content options are already localized; list options carry label_fr/label_en.
    // Keep the stored value separate from the label while the edit is queued.
    if (selection) {
      const label = getSelectionLabel(selection, value);
      cell.transformedValue = cell.prefix
        ? `${cell.prefix} ${label}`
        : cell.suffix
          ? `${label} ${cell.suffix}`
          : label;
    }
  } else if (cell.typeValue === 'multi-select') {
    cell.transformedValue = (Array.isArray(value) ? value : []).map((selectedValue) => {
      const selection = findSelection(selectedValue);
      return {
        value: selectedValue,
        label: getSelectionLabel(selection, selectedValue),
        color: selection?.color || 'primary',
        icon: selection?.icon || '',
        emoji: selection?.emoji || '',
      };
    });
  }

  if (cell.primaryData && cell.primaryData[key] !== undefined) {
    cell.primaryData[key] = value;
  }
};

export const getElementData = createAsyncThunk(
  'element/getElementData',
  async (
    {
      t,
      mode,
      accTabsPublic,
      accTabs,
      elementPath,
      structureId,
      currentlangCode,
      elementData,
      isRefresh = false,
      refreshCurrentIndex,
      refreshCurrentTab,
      activeTabIndex = 0,
      skipReadTracking = false,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      // Client keeps activeTabIndex as an array position in Redux; the API
      // expects the structureTabIndex (tab.index). Translate before calling.
      const tabMeta = getState().element.tabs.data?.[activeTabIndex];
      const apiActiveTabIndex =
        tabMeta?.index !== undefined && tabMeta?.index !== null
          ? tabMeta.index
          : activeTabIndex !== undefined && activeTabIndex !== null
            ? activeTabIndex
            : 0;

      const response = await nodeAxiosFirebase({
        url: `bosV4/single`,
        errorToast: (
          mode === 'PUBLIC' ? !accTabsPublic?.length : !accTabs?.length
        )
          ? t('errorLoadingDocument')
          : undefined,
        body: {
          type: mode,
          documentPath: elementPath,
          structureId: structureId,
          lang: currentlangCode?.slice(0, 2) || 'fr',
          document: elementData || null,
          height: window.innerHeight,
          width: window.innerWidth,
          device: 'desktop',
          forceRefreshBlockId: refreshCurrentIndex ?? null,
          forceRefreshTab: refreshCurrentTab ?? null,
          activeTabIndex: apiActiveTabIndex,
          ...(skipReadTracking === true ? { skipReadTracking: true } : {}),
        },
      });
      if (response?.errorData) {
        return rejectWithValue(response.errorData);
      }
      return { ...response, isRefresh };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Lazy-load all blocks for a given tab via bosV4/blocks.
 * No-op if the tab is already loaded or currently loading.
 */
export const loadTabBlocks = createAsyncThunk(
  'element/loadTabBlocks',
  async ({ tabIndex, lang }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const elementData = state.element.elementData;
      const mainElement = state.element.mainElement;

      const tabMeta = state.element.tabs.data?.[tabIndex];
      const existingTab = elementData?.[tabIndex];
      if (existingTab?.blocksLoaded && existingTab?.blocks?.length > 0) {
        return { skipped: true, tabIndex };
      }

      const blocksCount =
        tabMeta?.blocksCount ??
        existingTab?.blocksCount ??
        existingTab?.blocks?.length ??
        0;

      if (!blocksCount) {
        return { tabIndex, blocks: [], blocksLoaded: true };
      }

      // API expects structureTabIndex (tab.index); Redux keeps array position.
      const structureTabIndex =
        tabMeta?.index !== undefined && tabMeta?.index !== null
          ? tabMeta.index
          : existingTab?.index !== undefined && existingTab?.index !== null
            ? existingTab.index
            : tabIndex;

      const blocks = Array.from({ length: blocksCount }, (_, blockIndex) => ({
        tabIndex: structureTabIndex,
        blockIndex,
      }));

      const collectionField =
        mainElement?.elementStructureCollectionField || '';
      const documentPath = mainElement?.elementPath || (collectionField
        ? `${collectionField}/${mainElement?.elementId}`
        : null);

      const response = await nodeAxiosFirebase({
        url: `bosV4/blocks`,
        body: {
          documentPath,
          structureId: mainElement?.elementStructureId,
          blocks,
          lang: lang?.slice(0, 2) || 'fr',
          device: 'desktop',
          height: window.innerHeight,
          width: window.innerWidth,
        },
      });

      return { tabIndex, blocks: response?.blocks || [], blocksLoaded: true };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    // Runs BEFORE `pending` is dispatched, so it can safely read
    // tabs.loadingIndexes/loadedIndexes without racing this thunk's own
    // pending action (which would otherwise always see its own tabIndex
    // already marked as loading and skip every call).
    condition: ({ tabIndex }, { getState }) => {
      const state = getState();
      const loadingIndexes = state.element.tabs.loadingIndexes || [];
      const loadedIndexes = state.element.tabs.loadedIndexes || [];
      return !(
        loadedIndexes.includes(tabIndex) || loadingIndexes.includes(tabIndex)
      );
    },
  }
);

/**
 * Paginate a list block (load more / searchAll).
 * tabIndex = Redux array position; blockI = block.i identity.
 */
export const loadBlockDocuments = createAsyncThunk(
  'element/loadBlockDocuments',
  async (
    { tabIndex, blockI, searchAll = false, lang },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const mainElement = state.element.mainElement;
      const tab = state.element.elementData?.[tabIndex];
      const blocks = tab?.blocks || [];
      const block =
        (blockI != null && blocks.find((b) => b?.i === blockI)) || null;
      if (!block || block.contentType !== 'list') {
        return rejectWithValue('Invalid list block');
      }

      const structureTabIndex =
        tab?.index !== undefined && tab?.index !== null
          ? tab.index
          : state.element.tabs.data?.[tabIndex]?.index ?? tabIndex;
      const positionalIndex = blocks.findIndex((b) => b?.i === blockI);
      const structureBlockIndex =
        block.structureBlockIndex !== undefined &&
        block.structureBlockIndex !== null
          ? block.structureBlockIndex
          : positionalIndex;

      if (structureBlockIndex < 0) {
        return rejectWithValue('Invalid list block');
      }

      const collectionField =
        mainElement?.elementStructureCollectionField || '';
      const documentPath = mainElement?.elementPath || (collectionField
        ? `${collectionField}/${mainElement?.elementId}`
        : null);

      const response = await nodeAxiosFirebase({
        url: `bosV4/block-documents`,
        body: {
          documentPath,
          structureId: mainElement?.elementStructureId,
          tabIndex: structureTabIndex,
          blockIndex: structureBlockIndex,
          lang: lang?.slice(0, 2) || 'fr',
          device: 'desktop',
          height: window.innerHeight,
          width: window.innerWidth,
          startAfter: searchAll ? null : block.lastVisible || null,
          limit: block.pageSize || null,
          searchAll: Boolean(searchAll),
        },
      });

      return {
        tabIndex,
        blockI,
        searchAll: Boolean(searchAll),
        list: response?.list || [],
        lastVisible: response?.lastVisible ?? null,
        hasMore: Boolean(response?.hasMore),
        totalElements: response?.totalElements,
        pageSize: response?.pageSize,
        isRestricted: Boolean(response?.isRestricted),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getDemoElementData = createAsyncThunk(
  'element/getDemoElementData',
  async ({ structureId, elementData, lang }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `bosV4/generate-demo-element`,
        method: 'POST',
        body: {
          structureId,
          elementData,
          lang: lang?.slice(0, 2) || 'fr',
          height: window.innerHeight,
          width: window.innerWidth,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getSecondaryElementData = createAsyncThunk(
  'element/getSecondaryElementData',
  async (
    {
      t,
      elementPath,
      structureId,
      currentlangCode,
      signal,
      currentTab = null,
      blockId = null,
      blockStructureId = null,
      from = 'business',
      modelId = null,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `bosV4/singleDoc`,
        errorToast: t('errorLoadingDocument'),
        body: {
          documentPath: elementPath,
          lang: currentlangCode?.slice(0, 2) || 'fr',
          structureId: structureId,
          tab: currentTab ?? null,
          blockId: blockId ?? null,
          blockStructureId: blockStructureId ?? null,
          from: from,
          modelId: modelId ?? null,
        },
        signal,
      });

      if (response?.errorData) {
        return rejectWithValue(response.errorData);
      }
      return response;
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        error.code === 'ERR_CANCELED'
      ) {
        throw error;
      }

      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getElementPrimaryData = createAsyncThunk(
  'element/getElementPrimaryData',
  async ({ documentPath, structureId, currentlangCode }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        url: `bosV4/primary`,
        body: {
          documentPath,
          structureId,
          lang: currentlangCode?.slice(0, 2) || 'fr',
        },
      });
      if (response?.errorData) {
        return rejectWithValue(response.errorData);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Field edits on the same document made within this window are merged into a
// single coreSeqV6/bulk-edit call (processed as one Firestore transaction on
// the backend). These collections keep the one-field-at-a-time /single flow
// because their updates trigger parent/ledger recalculations.
const EXCLUDED_BATCH_COLLECTIONS = [
  'items',
  'payments',
  'valueaccounts',
  'logs',
  'movements',
];
const EDIT_BATCH_WINDOW_MS = 200;
const editBatchQueues = new Map();

// A caller (e.g. EditSidebar) can "hold" a document's batch open for the
// duration of an editing session: edits queue up without the usual 200ms
// auto-flush, and everything goes out as a single bulk-edit when the caller
// releases the hold (e.g. sidebar closed). MAX_SESSION_HOLD_MS is a safety
// net in case a caller forgets to release (e.g. missed cleanup).
const MAX_SESSION_HOLD_MS = 30000;
const heldEditSessions = new Map();

const isBatchExcluded = (elementPath) => {
  // Sandbox mode only remaps PATCH coreSeqV6/single: keep one call per field.
  if (isSandboxModeActive()) return true;
  const lastSegment = (elementPath || '').split('/').pop()?.toLowerCase();
  return EXCLUDED_BATCH_COLLECTIONS.includes(lastSegment);
};

export const holdEditSession = (elementPath, documentId) => {
  const batchKey = `${elementPath}::${documentId}`;
  if (heldEditSessions.has(batchKey)) return;

  // A queue may already exist (an edit fired just before the hold was
  // requested): cancel its auto-flush timer, it's now covered by the hold.
  const queue = editBatchQueues.get(batchKey);
  if (queue?.timeoutId) {
    clearTimeout(queue.timeoutId);
    queue.timeoutId = null;
  }

  const safetyTimeoutId = setTimeout(() => {
    heldEditSessions.delete(batchKey);
    flushEditBatch(batchKey);
  }, MAX_SESSION_HOLD_MS);
  heldEditSessions.set(batchKey, { safetyTimeoutId });
};

// Returns the flush promise so callers can wait for the real write to land
// before doing anything that depends on it (e.g. refreshing blocks) — edits
// queued during the hold resolve optimistically, well before this happens.
export const releaseEditSession = (elementPath, documentId) => {
  const batchKey = `${elementPath}::${documentId}`;
  const session = heldEditSessions.get(batchKey);
  if (!session) return Promise.resolve();
  clearTimeout(session.safetyTimeoutId);
  heldEditSessions.delete(batchKey);
  return flushEditBatch(batchKey);
};

// Flush a queue: 1 edit → /single (unchanged behavior), 2+ edits → one
// /bulk-edit whose per-edit results are redistributed to each pending promise
// in the /single response shape ({message, updatedDoc, timeStamp} or {errorData}).
const notifyPlanningEditRejection = (response, queue) => {
  const failure = response?.errorData || response;
  const codes = [failure?.code, failure?.message, ...(response?.results || []).flatMap(row => [row.code, row.error])];
  if (codes.includes('PLANNING_OVERPLAN_CONFIRMATION_REQUIRED')) toast.error(queue.planningOverplanMessage);
};

const flushEditBatch = async (batchKey) => {
  const queue = editBatchQueues.get(batchKey);
  editBatchQueues.delete(batchKey);
  if (!queue || queue.edits.length === 0) return;

  if (queue.edits.length === 1) {
    const { body, resolve, reject } = queue.edits[0];
    try {
      const response = await nodeAxiosFirebase({
        method: 'PATCH',
        url: `coreSeqV6/single`,
        errorToast: queue.errorToast,
        body,
      });
      notifyPlanningEditRejection(response, queue);
      resolve(response);
    } catch (error) {
      notifyPlanningEditRejection(error, queue);
      reject(error);
    }
    return;
  }

  try {
    const response = await nodeAxiosFirebase({
      method: 'PATCH',
      url: `coreSeqV6/bulk-edit`,
      errorToast: queue.errorToast,
      body: {
        edits: queue.edits.map(({ body }) => body),
      },
    });
    notifyPlanningEditRejection(response, queue);

    if (response?.errorData) {
      queue.edits.forEach(({ resolve }) => resolve(response));
      return;
    }

    queue.edits.forEach(({ resolve }, i) => {
      const result = response?.results?.find((r) => r?.index === i);
      if (result?.success) {
        resolve({
          message: result.message || '',
          updatedDoc: result.updatedDoc || {},
          timeStamp: response?.timeStamp,
        });
      } else {
        resolve({
          errorData: { message: result?.error || 'Update failed' },
        });
      }
    });
  } catch (error) {
    notifyPlanningEditRejection(error, queue);
    queue.edits.forEach(({ reject }) => reject(error));
  }
};

export const editElementValue = createAsyncThunk(
  'element/editElementValue',
  async ({ t, documentId, elementPath, value, key }, { rejectWithValue, getState }) => {
    const invalidateSecondaryLists = response => {
      if (response?.errorData || response?.status === 'error') return;
      const secondaryType = String(elementPath || '').match(/^businessesOnNode\/[^/]+\/(employees|locations|categories)(?:\/|$)/)?.[1];
      if (!secondaryType) return;
      for (const structure of getState().core.businessStructures || []) {
        if (structure.collectionField === 'secondary' && structure.secondaryType === secondaryType) invalidateStructureQueries(structure.id || structure.structureId);
      }
    };
    try {
      const body = { documentId, elementPath, value, key };

      if (isBatchExcluded(elementPath)) {
        const response = await nodeAxiosFirebase({
          method: 'PATCH',
          url: `coreSeqV6/single`,
          errorToast: t('errorUpdatingElement'),
          body,
        });
        invalidateSecondaryLists(response);
        return response;
      }

      const batchKey = `${elementPath}::${documentId}`;
      const isHeld = heldEditSessions.has(batchKey);

      let queue = editBatchQueues.get(batchKey);
      if (!queue) {
        queue = { edits: [], errorToast: t('errorUpdatingElement'), planningOverplanMessage: t('planningEditRequiresCalendar'), timeoutId: null };
        editBatchQueues.set(batchKey, queue);
        // Held sessions (e.g. EditSidebar open) flush explicitly on release
        // instead of on this fixed window (started at the first edit, not
        // reset on later edits).
        if (!isHeld) {
          queue.timeoutId = setTimeout(() => flushEditBatch(batchKey), EDIT_BATCH_WINDOW_MS);
        }
      }

      if (isHeld) {
        // The real request only goes out when the session is released (sidebar
        // closed/switched), which can be much later than this call. Resolve
        // optimistically now so callers (e.g. EditSidebar's per-field toast,
        // "saved" state, groups panel close) aren't stuck waiting for the
        // whole editing session to end. Failures at flush time still surface
        // via nodeAxiosFirebase's own errorToast.
        queue.edits.push({ body, resolve: invalidateSecondaryLists, reject: () => {} });
        return { message: 'Success', updatedDoc: {} };
      }

      const response = await new Promise((resolve, reject) => {
        queue.edits.push({ body, resolve, reject });
      });
      invalidateSecondaryLists(response);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createElement = createAsyncThunk(
  'element/createElement',
  async ({ t, elementPath, structureId, data, lang }, { rejectWithValue }) => {
    try {
      const response = await nodeAxiosFirebase({
        method: 'POST',
        url: `coreSeqV6/single`,
        errorToast: t('errorCreatingElement'),
        body: {
          elementPath,
          structureId,
          data,
          lang,
        },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const refreshBlocksByStructure = createAsyncThunk(
  'element/refreshBlocksByStructure',
  async (
    {
      targetStructureId,
      includeMainElement = false,
      lang,
      blockTypeFilter,
      retryUntilChanged = false,
      expectedElementPath,
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const elementData = state.element.elementData;
      const mainElement = state.element.mainElement;
      if (expectedElementPath && mainElement?.elementPath !== expectedElementPath) {
        return { blocks: [], mainElement: null };
      }

      // elementData is null while an element loads (or after a failed load):
      // nothing to refresh yet, and most callers fire-and-forget the unwrap.
      if (!elementData || !Array.isArray(elementData)) {
        return { blocks: [], mainElement: null };
      }

      // API expects structureTabIndex (tab.index) and structureBlockIndex;
      // Redux keeps array positions, which differ as soon as a tab or a block
      // is filtered out.
      const blocksToRefresh = [];
      elementData.forEach((tab, tabIndex) => {
        if (tab?.blocks) {
          const structureTabIndex =
            tab?.index ?? state.element.tabs.data?.[tabIndex]?.index ?? tabIndex;
          tab.blocks.forEach((block, blockIndex) => {
            const blockStructId = block?.structureId || block?.settings?.structure;
            if (blockStructId === targetStructureId) {
              if (blockTypeFilter && block?.type !== blockTypeFilter) return;
              blocksToRefresh.push({
                tabIndex: structureTabIndex,
                blockIndex: block?.structureBlockIndex ?? blockIndex,
              });
            }
          });
        }
      });

      if (blocksToRefresh.length === 0) {
        return { blocks: [], mainElement: null };
      }

      globalThrottleMap.set(targetStructureId, Date.now());

      const collectionField = mainElement?.elementStructureCollectionField || '';
      const documentPath = mainElement?.elementPath || (collectionField
        ? `${collectionField}/${mainElement?.elementId}`
        : null);
      const seq = ++blocksRefreshSeq;
      const orderKey = JSON.stringify([documentPath, targetStructureId, blockTypeFilter || null]);
      const applied = () => appliedBlocksRefresh.get(orderKey) || { blocks: 0, mainElement: 0 };

      const fetchBlocks = () =>
        nodeAxiosFirebase({
          url: `bosV4/blocks`,
          body: {
            documentPath,
            structureId: mainElement?.elementStructureId,
            targetStructureId,
            blocks: blocksToRefresh,
            lang: lang?.slice(0, 2) || 'fr',
            device: 'desktop',
            height: window.innerHeight,
            width: window.innerWidth,
            includeMainElement,
          },
        });

      let response = await fetchBlocks();

      // On a cold route, the refresh can return the previous (stale) list
      // before the created element is propagated. If the returned lists are
      // identical to the current ones, wait and refetch a couple of times.
      if (retryUntilChanged) {
        const previousLists = {};
        blocksToRefresh.forEach(({ tabIndex, blockIndex }) => {
          const slot = findBlockSlot(elementData, tabIndex, blockIndex);
          previousLists[`${tabIndex}-${blockIndex}`] = JSON.stringify(
            slot?.tab?.blocks?.[slot.blockPos]?.list ?? null
          );
        });

        const RETRY_DELAY_MS = 3000;
        const MAX_RETRIES = 2;
        let attempts = 0;

        while (attempts < MAX_RETRIES) {
          const hasChanged = (response?.blocks || []).some(
            ({ tabIndex, blockIndex, data }) =>
              previousLists[`${tabIndex}-${blockIndex}`] !==
              JSON.stringify(data?.list ?? null)
          );
          if (hasChanged || applied().blocks > seq) break;

          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          response = await fetchBlocks();
          attempts += 1;
        }
      }

      if (response?.errorData) return response;
      const done = applied();
      const writesBlocks = done.blocks < seq;
      const writesMainElement = !!response?.mainElement && done.mainElement < seq;
      appliedBlocksRefresh.set(orderKey, {
        blocks: writesBlocks ? seq : done.blocks,
        mainElement: writesMainElement ? seq : done.mainElement,
      });
      if (!writesBlocks || (response?.mainElement && !writesMainElement)) {
        return {
          ...response,
          blocks: writesBlocks ? response?.blocks : [],
          mainElement: writesMainElement ? response.mainElement : null,
        };
      }

      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  itemAdditions: [],
  // Successful native mutations/refreshes also invalidate addon query caches.
  dataRevision: 0,
  elementData: null,
  secondaryElementData: null,
  primaryPreview: null,
  tabs: {
    data: [],
    activeIndex: 0,
    loadedIndexes: [],
    loadingIndexes: [],
  },
  preferences: {},
  mainElement: {
    elementPath: '',
    elementId: '',
    elementName: '',
  },
  elementSelector: {
    selections: [],
    value: null,
  },
  activeFlow: null,
  loading: false,
  secondaryLoading: false,
  editLoading: false,
  createLoading: false,
  error: null,
  secondaryError: null,
  editError: null,
  createError: null,
  editSuccess: false,
  createSuccess: false,
};

const elementSlice = createSlice({
  name: 'element',
  initialState,
  reducers: {
    clearElementData: (state) => {
      state.elementData = null;
      state.secondaryElementData = null;
      state.primaryPreview = null;
      state.tabs = {
        data: [],
        activeIndex: 0,
        loadedIndexes: [],
        loadingIndexes: [],
      };
      state.preferences = {};
      state.mainElement = {
        elementPath: '',
        elementId: '',
        elementName: '',
      };
      state.elementSelector = { selections: [], value: null };
      state.activeFlow = null;
      state.loading = false;
      state.secondaryLoading = false;
      state.error = null;
      state.secondaryError = null;
    },
    queueItemAdditions: (state, action) => {
      state.itemAdditions = [...(state.itemAdditions || []), ...action.payload];
    },
    updateItemAddition: (state, action) => {
      const addition = state.itemAdditions?.find((entry) => entry.id === action.payload.id);
      if (addition) Object.assign(addition, action.payload);
    },
    removeItemAddition: (state, action) => {
      state.itemAdditions = (state.itemAdditions || []).filter((entry) => entry.id !== action.payload);
    },
    setElementSelector: (state, action) => {
      state.elementSelector = {
        ...state.elementSelector,
        ...action.payload,
      };
    },
    setElementSelectorValue: (state, action) => {
      state.elementSelector.value = action.payload;
    },
    setActiveTab: (state, action) => {
      state.tabs.activeIndex = action.payload;
    },
    setPreferences: (state, action) => {
      state.preferences = {
        history: false,
        email: false,
        print: false,
        add: false,
        edit: false,
        topTitle: true,
        delete: false,
        ...action.payload,
      };
    },
    setMainElement: (state, action) => {
      state.mainElement = action.payload;
    },
    setPrimaryPreview: (state, action) => {
      state.primaryPreview = action.payload;
    },
    clearPrimaryPreview: (state) => {
      state.primaryPreview = null;
    },
    setSecondaryElementData: (state, action) => {
      state.secondaryElementData = action.payload;
    },
    setActiveFlow: (state, action) => {
      state.activeFlow = action.payload;
    },
    advanceFlowStep: (state) => {
      if (state.activeFlow) {
        state.activeFlow = {
          ...state.activeFlow,
          currentStep: (state.activeFlow.currentStep || 0) + 1,
          completedSteps: [
            ...(state.activeFlow.completedSteps || []),
            state.activeFlow.currentStep || 0,
          ],
        };
      }
    },
    clearActiveFlow: (state) => {
      state.activeFlow = null;
    },
    setTabs: (state, action) => {
      // Navigation callers supply only metadata; keep the lazy-loading state
      // complete and discard indexes belonging to the previous tab list.
      state.tabs = {
        data: [],
        activeIndex: 0,
        loadedIndexes: [],
        loadingIndexes: [],
        ...action.payload,
      };
    },
    updateElementValue: (state, action) => {
      const { documentId, key, value } = action.payload;
      let finalValue = value;
      //if the value is a moment object, convert it to a string
      if (value instanceof moment) {
        finalValue = value.toISOString();
      }

      if (!state.elementData || !Array.isArray(state.elementData)) return;

      state.elementData.forEach((tab) => {
        if (tab.blocks) {
          tab.blocks.forEach((block) => {
            if (block.list && Array.isArray(block.list)) {
              block.list.forEach((row) => {
                if (row.data && Array.isArray(row.data)) {
                  row.data.forEach((cell) => {
                    if (
                      cell.primaryData?.elementId === documentId &&
                      cell.structureValue === key
                    ) {
                      cell.value = finalValue;
                      cell.transformedValue = finalValue;

                      if (
                        cell.primaryData &&
                        cell.primaryData[key] !== undefined
                      ) {
                        cell.primaryData[key] = finalValue;
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    },
    resetEditState: (state) => {
      state.editLoading = false;
      state.editError = null;
      state.editSuccess = false;
    },
    resetCreateState: (state) => {
      state.createLoading = false;
      state.createError = null;
      state.createSuccess = false;
    },
    updateBlocksByStructure: (state, action) => {
      const { blocks, mainElement } = action.payload;
      if (!state.elementData || !Array.isArray(state.elementData)) return;
      blocks.forEach(({ tabIndex, blockIndex, data }) => {
        const slot = findBlockSlot(state.elementData, tabIndex, blockIndex);
        if (!slot) return;
        slot.tab.blocks[slot.blockPos] = {
          ...slot.tab.blocks[slot.blockPos],
          ...data,
        };
      });
      if (mainElement) {
        state.mainElement = { ...state.mainElement, ...mainElement };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getElementData.pending, (state, action) => {
        state.loading = true;
        state.error = null;

        if (!action.meta.arg?.isRefresh && !action.meta.arg?.isPreview) {
          state.elementData = null;
          state.tabs.data = [];
          state.mainElement = {
            elementPath: '',
            elementId: '',
            elementName: '',
          };
        }
      })
      .addCase(getElementData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg?.isRefresh || action.payload?.isRefresh) {
          state.dataRevision = (state.dataRevision || 0) + 1;
        }

        if (action.payload?.tabs && Array.isArray(action.payload.tabs)) {
          // Eager tabs arrive complete: position == structureBlockIndex.
          state.elementData = action.payload.tabs.map((tab) => ({
            ...tab,
            blocks: (tab?.blocks || []).map((block, blockIndex) => ({
              ...block,
              structureBlockIndex:
                block?.structureBlockIndex !== undefined &&
                block?.structureBlockIndex !== null
                  ? block.structureBlockIndex
                  : blockIndex,
            })),
          }));

          state.tabs.data = action.payload.tabs.map(
            ({ blocks, ...tabDetails }) => tabDetails
          );

          // Mark tabs that arrived with blocks as loaded
          const loaded = [];
          action.payload.tabs.forEach((tab, idx) => {
            if (tab?.blocksLoaded || (tab?.blocks && tab.blocks.length > 0)) {
              loaded.push(idx);
            }
          });
          state.tabs.loadedIndexes = loaded;
          state.tabs.loadingIndexes = [];
        } else {
          state.elementData = action.payload;
        }

        if (action.payload?.preferences && !action.meta.arg?.isPreview) {
          state.preferences = action.payload.preferences;
        }
        if (action.payload?.mainElement) {
          state.mainElement = action.payload.mainElement;
        }
        state.primaryPreview = null;

        // Only reset tab index to 0 if this is NOT a refresh
        // Preserve current tab index when refreshing existing data
        if (!action.payload?.isRefresh) {
          const requested =
            action.meta.arg?.activeTabIndex !== undefined &&
            action.meta.arg?.activeTabIndex !== null
              ? action.meta.arg.activeTabIndex
              : 0;
          state.tabs.activeIndex = requested;
        } else {
          const tabCount = state.tabs.data?.length || 0;
          if (tabCount === 0) {
            state.tabs.activeIndex = 0;
          } else if (state.tabs.activeIndex >= tabCount) {
            state.tabs.activeIndex = tabCount - 1;
          }
        }
      })
      .addCase(loadTabBlocks.pending, (state, action) => {
        const tabIndex = action.meta.arg?.tabIndex;
        if (tabIndex === undefined || tabIndex === null) return;
        if (!state.tabs.loadingIndexes.includes(tabIndex)) {
          state.tabs.loadingIndexes.push(tabIndex);
        }
      })
      .addCase(loadTabBlocks.fulfilled, (state, action) => {
        const { tabIndex, blocks, skipped, blocksLoaded } = action.payload || {};
        state.tabs.loadingIndexes = (state.tabs.loadingIndexes || []).filter(
          (i) => i !== tabIndex
        );
        if (skipped) {
          if (!state.tabs.loadedIndexes.includes(tabIndex)) {
            state.tabs.loadedIndexes.push(tabIndex);
          }
          return;
        }
        if (!state.elementData || !Array.isArray(state.elementData)) return;

        // Ensure tab slot exists
        if (!state.elementData[tabIndex]) {
          state.elementData[tabIndex] = {
            ...(state.tabs.data?.[tabIndex] || {}),
            blocks: [],
          };
        }

        const count =
          state.tabs.data?.[tabIndex]?.blocksCount ??
          (blocks || []).length ??
          0;
        const nextBlocks = new Array(count).fill(null);
        (blocks || []).forEach(({ blockIndex, data }) => {
          if (data && blockIndex >= 0 && blockIndex < count) {
            nextBlocks[blockIndex] = {
              ...data,
              structureBlockIndex: blockIndex,
            };
          }
        });

        state.elementData[tabIndex] = {
          ...state.elementData[tabIndex],
          ...state.tabs.data?.[tabIndex],
          blocks: nextBlocks.filter(Boolean),
          blocksLoaded: blocksLoaded !== false,
        };

        if (!state.tabs.loadedIndexes.includes(tabIndex)) {
          state.tabs.loadedIndexes.push(tabIndex);
        }
      })
      .addCase(loadTabBlocks.rejected, (state, action) => {
        const tabIndex = action.meta.arg?.tabIndex;
        state.tabs.loadingIndexes = (state.tabs.loadingIndexes || []).filter(
          (i) => i !== tabIndex
        );
      })
      .addCase(loadBlockDocuments.fulfilled, (state, action) => {
        const {
          tabIndex,
          blockI,
          searchAll,
          list,
          lastVisible,
          hasMore,
          totalElements,
          pageSize,
        } = action.payload || {};
        const blocks = state.elementData?.[tabIndex]?.blocks || [];
        const block =
          blockI != null ? blocks.find((b) => b?.i === blockI) : null;
        if (!block) return;

        if (searchAll) {
          block.list = list;
          block.hasMore = false;
          block.lastVisible = null;
        } else {
          block.list = [...(block.list || []), ...(list || [])];
          block.hasMore = hasMore;
          block.lastVisible = lastVisible;
        }
        if (totalElements != null) block.totalElements = totalElements;
        if (pageSize != null) block.pageSize = pageSize;
      })
      .addCase(getElementData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDemoElementData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDemoElementData.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.tabs && Array.isArray(action.payload.tabs)) {
          state.elementData = action.payload.tabs;
          state.tabs.data = action.payload.tabs.map(
            ({ blocks, ...tabDetails }) => tabDetails
          );
        } else {
          state.elementData = action.payload;
        }

        if (action.payload?.preferences) {
          state.preferences = action.payload.preferences;
        }
        if (action.payload?.mainElement) {
          state.mainElement = action.payload.mainElement;
        }

        state.tabs.activeIndex = 0;
      })
      .addCase(getDemoElementData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSecondaryElementData.pending, (state) => {
        state.secondaryLoading = true;
        state.secondaryError = null;
      })
      .addCase(getSecondaryElementData.fulfilled, (state, action) => {
        state.secondaryLoading = false;
        state.secondaryElementData = action.payload;
      })
      .addCase(getSecondaryElementData.rejected, (state, action) => {
        state.secondaryLoading = false;
        state.secondaryError = action.payload;
      })
      .addCase(getElementPrimaryData.fulfilled, (state, action) => {
        if (action.payload?.primaryElement) {
          state.primaryPreview = action.payload.primaryElement;
        }
        if (action.payload?.mainElement) {
          state.mainElement = {
            ...state.mainElement,
            ...action.payload.mainElement,
          };
        }
      })
      .addCase(editElementValue.pending, (state) => {
        state.editLoading = true;
        state.editError = null;
        state.editSuccess = false;
      })
      .addCase(editElementValue.fulfilled, (state, action) => {
        state.editLoading = false;
        state.editSuccess = true;
        if (!action.payload?.errorData) state.dataRevision = (state.dataRevision || 0) + 1;

        // Update the element data directly in state instead of refreshing
        const { meta } = action;
        if (meta && meta.arg) {
          const { documentId, key, value, lang } = meta.arg;
          const language = lang?.slice(0, 2);

          let finalValue = value;
          //if the value is a moment object, convert it to a string
          if (value instanceof moment) {
            finalValue = value.toISOString();
          }

          if (!state.elementData || !Array.isArray(state.elementData)) return;

          state.elementData.forEach((tab) => {
            if (tab.blocks) {
              tab.blocks.forEach((block) => {
                if (block.list && Array.isArray(block.list)) {
                  block.list.forEach((row) => {
                    if (row.data && Array.isArray(row.data)) {
                      row.data.forEach((cell) => {
                        if (
                          cell.primaryData?.elementId === documentId &&
                          cell.structureValue === key
                        ) {
                          updateEditedCell(cell, key, finalValue, language);
                        }
                      });
                    }
                  });
                }
                if (block.content && Array.isArray(block.content)) {
                  block.content.forEach((cell) => {
                    if (
                      cell.primaryData?.elementId === documentId &&
                      cell.structureValue === key
                    ) {
                      updateEditedCell(cell, key, finalValue, language);
                    }
                  });
                }
              });
            }
          });
        }

        // Also update secondaryElementData if needed
        if (state.secondaryElementData && action.payload) {
          // Update the specific field that was edited
          // This depends on your API response structure
        }
      })
      .addCase(editElementValue.rejected, (state, action) => {
        state.editLoading = false;
        state.editError = action.payload;
        state.editSuccess = false;
      })
      .addCase(createElement.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.createSuccess = false;
      })
      .addCase(createElement.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        if (!action.payload?.errorData) state.dataRevision = (state.dataRevision || 0) + 1;
      })
      .addCase(createElement.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload;
        state.createSuccess = false;
      })
      .addCase(refreshBlocksByStructure.fulfilled, (state, action) => {
        const expectedPath = action.meta.arg.expectedElementPath;
        if (expectedPath && state.mainElement?.elementPath !== expectedPath) return;
        if (!action.payload?.errorData) state.dataRevision = (state.dataRevision || 0) + 1;
        if (!state.elementData || !Array.isArray(state.elementData)) return;
        const { blocks, mainElement } = action.payload || {};
        if (blocks) {
          blocks.forEach(({ tabIndex, blockIndex, data }) => {
            const slot = findBlockSlot(state.elementData, tabIndex, blockIndex);
            if (!slot) return;
            slot.tab.blocks[slot.blockPos] = {
              ...slot.tab.blocks[slot.blockPos],
              ...data,
            };
          });
        }
        if (mainElement) {
          state.mainElement = { ...state.mainElement, ...mainElement };
        }
      });
  },
});

export const {
  queueItemAdditions,
  updateItemAddition,
  removeItemAddition,
  clearElementData,
  setActiveTab,
  setPreferences,
  setSecondaryElementData,
  resetEditState,
  resetCreateState,
  setMainElement,
  setPrimaryPreview,
  clearPrimaryPreview,
  setTabs,
  updateElementValue,
  updateBlocksByStructure,
  setElementSelector,
  setElementSelectorValue,
  setActiveFlow,
  advanceFlowStep,
  clearActiveFlow,
} = elementSlice.actions;
export default elementSlice.reducer;

export const refreshSecondaryRecord = (secondaryType) => (dispatch, getState) => {
  const state = getState();
  for (const structure of state.core.businessStructures || []) {
    if (structure.collectionField === 'secondary' && structure.secondaryType === secondaryType) invalidateStructureQueries(structure.id || structure.structureId);
  }
  const main = state.element.mainElement;
  const structureId = main?.elementStructureId || main?.structureId;
  if (main?.secondaryType !== secondaryType || !structureId) return;
  return dispatch(refreshBlocksByStructure({ targetStructureId: structureId, includeMainElement: true }));
};
